import React, { useEffect, useRef, useState } from 'react';
import IconButton from './components/IconButton';
import EditActionIcon from './components/EditActionIcon';
import TrashActionIcon from './components/TrashActionIcon';
import ViewActionIcon from './components/ViewActionIcon';
import { getProductMarkets } from './utils/productForm';
import ProductPreviewCard from './ProductPreviewCard';
import NoProductImage from "./components/NoProductImage";
import { checkPermissionActions } from './utils/permissions';
import { getAdminAction, getAvailableActions, getWriteActions } from './utils/permissionCatalog';

export default function ProductTiles({ products, brands, markets, onEdit, onArchive, currentUser }) {
  const availableActions = getAvailableActions(currentUser);
  const canEditProducts = checkPermissionActions(availableActions, getWriteActions('Products'));
  const canArchiveProducts = checkPermissionActions(availableActions, getAdminAction('Products'));
  const [openPreviewId, setOpenPreviewId] = useState<string | null>(null);
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef<number | null>(null);

  const openProduct = openPreviewId
    ? (products || []).find((p) => String(p.id) === String(openPreviewId))
    : null;

  const closePreview = () => {
    if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = null;
    setIsClosing(false);
    setOpenPreviewId(null);
  };

  const closePreviewAnimated = () => {
    if (!openPreviewId) return;
    if (isClosing) return;

    setIsClosing(true);

    if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = window.setTimeout(() => {
      setOpenPreviewId(null);
      setIsClosing(false);
      closeTimeoutRef.current = null;
    }, 180);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) window.clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!openPreviewId) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePreviewAnimated();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [openPreviewId, isClosing]);

  return (
    <>
      <div className="tile-grid" data-testid="products-tile-grid">
        {products.length === 0 && (
          <div data-testid="products-tile-grid-empty">No products found.</div>
        )}
        {products.map((p) => (
          // keep ids stable across number/string
           
          <div key={p.id} className={`tile-card${p.isArchived ? ' inactive' : ''}`} data-testid={`product-tile-${p.id}`}>
            {
              p.productImageUrl ?
                <div className="tile-image" style={{ backgroundImage: `url(${p.productImageUrl || ''})` }} data-testid={`product-tile-image-${p.id}`} />
                :
                <div className="tile-image" style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}} data-testid={`product-tile-image-empty-${p.id}`}>
                  <NoProductImage scale={2} />
                </div>
            }
            <div className="tile-body">
              <div className="tile-title-row">
                <div style={{ minWidth: 0 }}>
                  <div
                    className="muted clamp-1"
                    style={{ fontSize: 12 }}
                    title={String(p.brand || '')}
                    data-testid={`product-tile-brand-${p.id}`}
                  >
                    {p.brand}
                  </div>
                  <div className="tile-title clamp-2" title={String(p.name || '')} data-testid={`product-tile-name-${p.id}`}>{p.name}</div>
                </div>
              </div>
              <div className="row-content">
                {getProductMarkets(p).map((m) => (
                  <span key={m} className="tag">{m}</span>
                ))}
              </div>
              {p.category ? (<div className="tile-meta">
                <span className="tag subtle">{p.category}</span>
              </div>) : ''}
              <div className="tile-actions">
                <IconButton
                  title="View"
                  ariaLabel={`View ${p.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsClosing(false);
                    setOpenPreviewId(String(p.id));
                  }}
                  testId={`product-view-${p.id}`}
                >
                  <ViewActionIcon />
                </IconButton>

                {canEditProducts && <IconButton
                  title="Edit"
                  ariaLabel={`Edit ${p.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    closePreview();
                    if(onEdit) onEdit(p);
                  }}
                  testId={`product-edit-${p.id}`}
                >
                  <EditActionIcon />
                </IconButton>}

                {!p.isArchived && canArchiveProducts ? (
                  <IconButton
                    title="Archive"
                    ariaLabel={`Archive ${p.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      closePreview();
                      if(onArchive) onArchive(p);
                    }}
                    testId={`product-archive-${p.id}`}
                  >
                    <TrashActionIcon />
                  </IconButton>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>

      {openProduct ? (
        <div
          className="modal-backdrop product-preview-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={`Preview ${openProduct.name || 'product'}`}
          data-testid={`product-preview-modal-${openProduct.id}`}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreviewAnimated();
          }}
        >
          <div
            className={`modal product-preview-drawer page-slide-in${isClosing ? ' page-slide-out' : ''}`}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2 className="modal-title">Product Preview</h2>
              <button className="subtle-btn" onClick={closePreviewAnimated} data-testid={`product-preview-close-${openProduct.id}`}>Close</button>
            </div>

            <div className="product-preview-drawer-body">
              <ProductPreviewCard
                id={openProduct.id}
                name={openProduct.name}
                brand={ brands.find(b => b.id === openProduct.brandId)?.name || '' }
                category={openProduct.productCategoryName || ''}
                strain={openProduct.strain}
                inventoryUpc={openProduct.inventoryUpc}
                imageName={openProduct.imageName || (typeof openProduct.image === 'string' ? openProduct.image.split('/').pop() : '') || ''}
                imageUrl={typeof openProduct.productImageUrl === 'string' ? openProduct.productImageUrl : ''}
                markets={ markets.filter(m => openProduct.marketIds.includes(m.id)).map(m => m.name || m.label || m.abbreviation || String(m.id)) }
                description={openProduct.description}
                isArchived={Boolean(openProduct.isArchived)}
                archivedAt={openProduct.archivedAt}
                showStatusBadge
                emptyNameLabel="—"
                container="card"
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
